import { useState } from "react";
import { Building2, CalendarDays, FileBarChart, ChevronLeft, ChevronRight } from "lucide-react";
import { SITE } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { PhotoSlot } from "@/components/site/Photo";
import { Reveal, SectionHeading, CtaLink, DonateButton } from "@/components/site/Primitives";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FRANK_TURNER } from "@/content/content";

const GalleryItem = ({ p, i, onOpen }) =>
  p.src ? (
    <figure>
      <button type="button" onClick={() => onOpen(i)} aria-label={`Enlarge photo: ${p.caption}`} className="group relative block aspect-[4/3] w-full overflow-hidden bg-mist" data-testid={`frank-photo-${i + 1}`}>
        <img src={p.src} alt={p.alt || p.caption} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ objectPosition: p.position || "center" }} />
      </button>
      <figcaption className="mt-3 text-xs leading-relaxed text-slate">{p.caption}</figcaption>
    </figure>
  ) : (
    <PhotoSlot ratio="aspect-[4/3]" label={p.label} caption={p.caption} testId={`frank-photo-${i + 1}`} />
  );

const Memorial = () => {
  const [idx, setIdx] = useState(null);
  const photos = FRANK_TURNER.photos.filter((p) => p.src);
  const featured = FRANK_TURNER.photos.slice(0, 6);
  const open = idx === null ? null : photos[idx];
  const step = (d) => setIdx((v) => (v + d + photos.length) % photos.length);
  return (
    <section id="frank-turner" className="py-24 md:py-32" data-testid="frank-turner-memorial">
      <div className="container-x grid gap-14 lg:grid-cols-[1fr_1fr] lg:gap-20">
        <div className="lg:sticky lg:top-32 lg:self-start">
          <p className="eyebrow">In Memoriam</p>
          <h2 className="mt-5 text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">{FRANK_TURNER.title}</h2>
          <div className="mt-8 grid grid-cols-3 gap-px border border-navy/10 bg-navy/10">
            {FRANK_TURNER.facts.map((f) => (
              <div key={f.k} className="bg-white p-5">
                <p className="font-display text-xl font-black text-navy md:text-2xl">{f.v}</p>
                <p className="mt-2 text-xs font-semibold leading-snug text-slate">{f.k}</p>
              </div>
            ))}
          </div>
        </div>
        <Reveal className="space-y-5 text-base leading-relaxed text-ink/85 md:text-lg" data-testid="frank-turner-story">
          {FRANK_TURNER.story.map((p) => <p key={p}>{p}</p>)}
          <p className="border-l-2 border-gold pl-5 text-base text-ink/80" data-testid="frank-foam-recognition">
            With heartfelt thanks to{" "}
            <a href="https://www.foamusa.org" target="_blank" rel="noopener noreferrer" className="font-semibold text-navy underline decoration-gold decoration-2 underline-offset-4 transition-colors hover:text-gold-deep" data-testid="frank-foam-link">
              Firefighters on a Mission<span className="sr-only"> (opens in a new tab)</span>
            </a>{" "}
            for helping secure the wheelchair-accessible van for Frank and Becky Turner.
          </p>
        </Reveal>
      </div>
      <div className="container-x mt-20">
        <h3 className="text-xl font-bold">Photographs</h3>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="frank-turner-gallery">
          {featured.map((p, i) => <GalleryItem key={i} p={p} i={i} onOpen={setIdx} />)}
        </div>
        {photos.length > featured.length && (
          <button type="button" onClick={() => setIdx(0)} className="btn btn-ghost mt-10" data-testid="frank-view-all-photos-button">View All {photos.length} Photos</button>
        )}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setIdx(null)}>
        <DialogContent className="max-w-4xl border-none bg-navy p-3 text-paper" data-testid="frank-photo-dialog">
          <DialogTitle className="sr-only">{open?.caption}</DialogTitle>
          {open && <img src={open.src} alt={open.alt || open.caption} className="max-h-[75vh] w-full object-contain" />}
          <div className="flex items-center justify-between gap-4 px-2 pb-1">
            <p className="text-sm text-[#D5DEE7]">{open?.caption}</p>
            <div className="flex shrink-0 items-center gap-2">
              <button type="button" onClick={() => step(-1)} aria-label="Previous photo" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 hover:bg-white/10" data-testid="frank-photo-prev"><ChevronLeft size={18} /></button>
              <span className="text-xs text-[#C9D4DF]">{idx + 1} / {photos.length}</span>
              <button type="button" onClick={() => step(1)} aria-label="Next photo" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 hover:bg-white/10" data-testid="frank-photo-next"><ChevronRight size={18} /></button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const BLOCKS = [
  { icon: Building2, t: "Agency Partnerships", d: "Fire, law enforcement, and public safety agencies that work alongside the Foundation will be recognized here with their approval." },
  { icon: CalendarDays, t: "Community Events", d: "Highlights from Foundation events, fundraisers, and community appearances with Luna." },
  { icon: FileBarChart, t: "Annual Impact Updates", d: "Yearly summaries of program activity and milestones, published once reviewed and approved." },
];

const Blocks = () => (
  <section className="border-t border-navy/10 bg-mist py-24 md:py-32" data-testid="impact-updates-section">
    <div className="container-x">
      <SectionHeading eyebrow="Looking Ahead" title="More Stories to Come." intro="This page will grow as the Foundation documents its work. We share only stories, figures, and photographs that have been verified and approved by the people involved." />
      <div className="mt-14 grid gap-5 lg:grid-cols-3">
        {BLOCKS.map((b, i) => (
          <Reveal key={b.t} delay={i * 0.08} className="flex flex-col border border-navy/10 bg-paper p-8 md:p-10" data-testid={`impact-block-${i + 1}`}>
            <b.icon className="text-gold-deep" size={28} />
            <h3 className="mt-8 text-2xl font-extrabold">{b.t}</h3>
            <p className="mt-4 flex-1 leading-relaxed text-ink/80">{b.d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Impact() {
  return (
    <>
      <Seo title="Our Impact | First Responders Foundation, Palm Beach County" description="Remembering Frank Turner, who volunteered as Santa for 20+ years, and the community that came together for him. Stories from First Responders Foundation." path="/impact" />
      <PageHero eyebrow="Our Impact" lines={[{ text: "Every Story Matters." }, { text: "Every Act of Support", className: "text-gold" }, { text: "Counts.", className: "text-gold" }]} intro="Our impact is measured in people: the responders we stand beside, and the neighbors who show up for one another when it matters most." />
      <Memorial />
      <Blocks />
      <section className="bg-navy py-20 text-paper" data-testid="impact-cta-section">
        <div className="container-x flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-2xl text-2xl font-extrabold text-paper md:text-4xl">Help write the next chapter.</h2>
          <div className="flex flex-col gap-3 sm:flex-row">
            <DonateButton label="Donate Now" href={SITE.donateLinks.general} testId="impact-donate-button" />
            <CtaLink to="/get-involved" variant="btn-ghost-light" testId="impact-get-involved-button">Get Involved</CtaLink>
          </div>
        </div>
      </section>
    </>
  );
}
